/**
 * BuildInfoTile — super-admin Admin Dashboard tile that surfaces
 * the current build number, git commit SHA, and build timestamp.
 *
 * Source: GET /api/admin/build-info, which reads /app/BUILD_NUMBER
 * (auto-incremented by the pre-commit hook on every commit). Lets
 * a super-admin instantly answer "what build is this user on?"
 * when triaging a field report.
 *
 * Hidden for non-super-admins (mirrors EngineeringHealthTile's
 * `error === 'forbidden'` short-circuit).
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Hash, GitCommit, Clock } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';


function _relativeTime(iso) {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const seconds = Math.round((Date.now() - ts) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}


export default function BuildInfoTile() {
  const { data, loading, error } = useTelemetryTile({
    url: '/admin/build-info',
    deps: [],
  });

  if (error === 'forbidden') return null;

  return (
    <Card
      data-testid="build-info-tile"
      className="border-slate-200 bg-gradient-to-br from-white to-slate-50/60"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Hash className="h-4 w-4 text-slate-500" />
          Build Info
        </CardTitle>
        <CardDescription className="text-xs">
          Current shipped build for this environment
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-1">
        {loading && (
          <p className="text-sm text-slate-400">Loading…</p>
        )}
        {error && error !== 'forbidden' && (
          <p className="text-sm text-rose-600" data-testid="build-info-error">
            Failed to load build info.
          </p>
        )}
        {data && !loading && !error && (
          <div className="space-y-2.5">
            <div className="flex items-baseline gap-2" data-testid="build-info-number">
              <span className="text-3xl font-extrabold text-slate-900 tabular-nums">
                #{data.build_number ?? 0}
              </span>
              <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                build
              </span>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-slate-600 font-mono" data-testid="build-info-commit">
              <GitCommit className="h-3 w-3 text-slate-400" />
              <span>{data.git_commit || 'unknown'}</span>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="build-info-built-at">
              <Clock className="h-3 w-3 text-slate-400" />
              <span title={data.built_at}>built {_relativeTime(data.built_at)}</span>
            </div>

            <div className="pt-2 mt-1 border-t border-slate-100 text-[10px] text-slate-400 flex flex-wrap gap-x-3 gap-y-0.5" data-testid="build-info-runtime">
              <span>boot {_relativeTime(data.boot_at)}</span>
              <span>py {data.python_version}</span>
              {data.platform && <span>{data.platform.toLowerCase()}</span>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
